import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import GlobalComponent from "../GlobalComponents";
import {
    FaHome, FaUserShield, FaExclamationTriangle, FaCog, FaArrowLeft,
    FaChartBar, FaGlobe
} from 'react-icons/fa';
import { useNavigation } from "../../Navigation";
import { Menu, MenuItem, SubMenu, useProSidebar } from 'react-pro-sidebar';
import {BackButton, componentMap, designTokens, MainContent, StyledSidebar} from "./components";


const DynamicPageLayout = () => {
    const { menuName } = useParams();
    const { navigation, isLoading } = useNavigation();
    const { leftSideBarType } = useSelector((state) => state.Layout);
    const sidebarRef = useRef(null);
    
    // Initialize state with localStorage values or defaults
    const [selectedModule, setSelectedModule] = useState(() => {
        return localStorage.getItem('selectedModule') || null;
    });
    
    const [selectedSubmenu, setSelectedSubmenu] = useState(() => {
        return localStorage.getItem('selectedSubmenu') || null;
    });
    
    const [selectedSubmenuRoute, setSelectedSubmenuRoute] = useState(() => {
        return localStorage.getItem('lastSubmenuRoute') || null;
    });
    
    // Track open/expanded submenus
    const [openSubmenus, setOpenSubmenus] = useState(() => {
        const savedOpenSubmenus = localStorage.getItem('openSubmenus');
        return savedOpenSubmenus ? JSON.parse(savedOpenSubmenus) : [localStorage.getItem('selectedSubmenu')];
    });
    
    // Add state to track hover state
    const [isHoverExpanded, setIsHoverExpanded] = useState(false);
    
    const navigate = useNavigate();
    const { collapseSidebar, collapsed } = useProSidebar();

    useEffect(() => {
        if (leftSideBarType === "condensed" && !collapsed) {
            collapseSidebar();
        } else if (leftSideBarType !== "condensed" && collapsed) {
            collapseSidebar();
        }

        const handleMouseEnter = () => {
            if ((leftSideBarType === "condensed" || collapsed) && sidebarRef.current) {
                setIsHoverExpanded(true);
                sidebarRef.current.classList.add('hover-expanded');
            }
        };

        const handleMouseLeave = () => {
            if ((leftSideBarType === "condensed" || collapsed) && sidebarRef.current) {
                setIsHoverExpanded(false);
                sidebarRef.current.classList.remove('hover-expanded');
                // Ensure sidebar remains collapsed after hover
                if (!collapsed) {
                    collapseSidebar(true);
                }
            }
        };

        const sidebarElement = sidebarRef.current;
        if (sidebarElement) {
            sidebarElement.addEventListener('mouseenter', handleMouseEnter);
            sidebarElement.addEventListener('mouseleave', handleMouseLeave);
        }

        return () => {
            if (sidebarElement) {
                sidebarElement.removeEventListener('mouseenter', handleMouseEnter);
                sidebarElement.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, [leftSideBarType, collapsed, collapseSidebar]);

    const groupedNavigation = useMemo(() => {
        return navigation.reduce((acc, item) => {
            if (!acc[item.menu_name]) {
                acc[item.menu_name] = {};
            }
            if (!acc[item.menu_name][item.submenu_name]) {
                acc[item.menu_name][item.submenu_name] = [];
            }
            acc[item.menu_name][item.submenu_name].push({
                module_name: item.module_name,
                module_route: item.module_route,
                submenu_route: item.submenu_route
            });
            return acc;
        }, {});
    }, [navigation]);

    const uniqueMenus = useMemo(() =>
            [...new Set(navigation.map(item => item.menu_name))],
        [navigation]
    );

    const currentMenu = useMemo(() => {
        if (!menuName) {
            // If no menuName is provided in the URL but we have stored values,
            // try to redirect to the correct menu based on stored submenu route
            const storedRoute = localStorage.getItem('lastSubmenuRoute');
            if (storedRoute && navigation.length > 0) {
                const navItem = navigation.find(item => item.module_route === storedRoute);
                if (navItem) {
                    // Redirect to the correct menu page
                    navigate(`/${navItem.menu_name.toLowerCase().replace(/ /g, '-')}`);
                    return navItem.menu_name;
                }
            }
            return null;
        }
        
        const formattedMenuName = menuName.replace(/-/g, ' ');
        const foundMenu = Object.keys(groupedNavigation).find(
            menu => menu.toLowerCase() === formattedMenuName.toLowerCase()
        );
        
        // If we've switched to a new menu, check if the stored route is valid for this menu
        if (foundMenu) {
            const storedModule = localStorage.getItem('selectedModule');
            const storedSubmenu = localStorage.getItem('selectedSubmenu');
            const storedRoute = localStorage.getItem('lastSubmenuRoute');
            
            if (storedRoute) {
                // Check if the stored route exists in the current menu context
                const routeExistsInCurrentMenu = navigation.some(item => 
                    item.module_route === storedRoute && 
                    item.menu_name.toLowerCase() === formattedMenuName.toLowerCase()
                );
                
                // If the stored route is not in the current menu, clear it
                if (!routeExistsInCurrentMenu) {
                    // We'll reset the stored state in the useEffect that follows
                    // console.log("Stored route not found in current menu, will reset selection");
                }
            }
        }
        
        return foundMenu;
    }, [menuName, groupedNavigation, navigate, navigation]);

    // Update state from localStorage or set defaults when navigation is loaded
    useEffect(() => {
        if (navigation.length > 0 && currentMenu) {
            const storedModule = localStorage.getItem('selectedModule');
            const storedSubmenu = localStorage.getItem('selectedSubmenu');
            const storedRoute = localStorage.getItem('lastSubmenuRoute');
            
            // Check if the stored route belongs to the current menu
            const routeExistsInCurrentMenu = storedRoute && navigation.some(item => 
                item.module_route === storedRoute && 
                item.menu_name.toLowerCase() === currentMenu.toLowerCase()
            );
            
            // If we have a complete stored state AND it belongs to the current menu
            if (storedModule && storedSubmenu && storedRoute && routeExistsInCurrentMenu) {
                // Verify the stored values exist in the current navigation
                const moduleExists = navigation.some(item => 
                    item.module_name === storedModule && 
                    item.submenu_name === storedSubmenu &&
                    item.module_route === storedRoute
                );
                
                if (moduleExists) {
                    // If the stored module exists, use it
                    setSelectedModule(storedModule);
                    setSelectedSubmenu(storedSubmenu);
                    setSelectedSubmenuRoute(storedRoute);
                    return;
                }
            }
            
            // Fallback to default selection logic if stored values don't exist, are invalid, or belong to a different menu
            if (currentMenu && groupedNavigation[currentMenu]) {
                const firstSubmenu = Object.keys(groupedNavigation[currentMenu])[0];
                const firstModule = groupedNavigation[currentMenu][firstSubmenu][0];
                
                setSelectedSubmenu(firstSubmenu);
                setSelectedModule(firstModule.module_name);
                setSelectedSubmenuRoute(firstModule.module_route);
                
                // Store the new values
                localStorage.setItem('selectedModule', firstModule.module_name);
                localStorage.setItem('selectedSubmenu', firstSubmenu);
                localStorage.setItem('lastSubmenuRoute', firstModule.module_route);
                
                // Update open submenus
                if (!openSubmenus.includes(firstSubmenu)) {
                    const newOpenSubmenus = [...openSubmenus, firstSubmenu].filter(Boolean);
                    setOpenSubmenus(newOpenSubmenus);
                    localStorage.setItem('openSubmenus', JSON.stringify(newOpenSubmenus));
                }
            }
        }
    }, [currentMenu, groupedNavigation, navigation, openSubmenus]);

    const handleModuleSelect = (submenuName, moduleName, moduleRoute) => {
        setSelectedSubmenu(submenuName);
        setSelectedModule(moduleName);
        setSelectedSubmenuRoute(moduleRoute);
        
        // Store selected values in localStorage
        localStorage.setItem('selectedModule', moduleName);
        localStorage.setItem('selectedSubmenu', submenuName);
        localStorage.setItem('lastSubmenuRoute', moduleRoute);
        
        // Make sure the submenu is open
        if (!openSubmenus.includes(submenuName)) {
            const newOpenSubmenus = [...openSubmenus, submenuName];
            setOpenSubmenus(newOpenSubmenus);
            localStorage.setItem('openSubmenus', JSON.stringify(newOpenSubmenus));
        }
    };

    const handleMenuSelect = (selectedMenuName) => {
        navigate(`/${selectedMenuName.toLowerCase().replace(/ /g, '-')}`);
    };

    const getIcon = (menuName) => {
        switch (menuName.toLowerCase()) {
            case 'dashboard': return <FaHome />;
            case 'human resources': return <FaUserShield />;
            case 'incident': return <FaExclamationTriangle />;
            case 'reports': return <FaChartBar />;
            default: return <FaCog />;
        }
    };

    const getSubMenuIcon = (moduleRoute) => {
        const key = Object.keys(componentMap).find(k =>
            moduleRoute.toLowerCase().includes(k.toLowerCase())
        );
        const IconComponent = key ? componentMap[key].icon : FaCog;
        return <IconComponent />;
    };

    // Handle submenu toggle
    const handleSubmenuToggle = (submenuName, open) => {
        setOpenSubmenus(prev => {
            const newOpenSubmenus = open 
                ? [...prev, submenuName].filter((v, i, a) => a.indexOf(v) === i) // Add and deduplicate
                : prev.filter(name => name !== submenuName); // Remove
            
            // Save to localStorage
            localStorage.setItem('openSubmenus', JSON.stringify(newOpenSubmenus));
            return newOpenSubmenus;
        });
    };

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#f5f7fa'
            }}>
                <div style={{
                    background: '#fff',
                    padding: '2.5rem 2rem',
                    borderRadius: '1.25rem',
                    boxShadow: '0 4px 24px rgba(1,62,125,0.07)',
                    textAlign: 'center',
                    maxWidth: '400px',
                    border: '1.5px solid #e3ecf7'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: '3px solid #e3ecf7',
                        borderTop: '3px solid #013e7d',
                        borderRadius: '50%',
                        margin: '0 auto 20px',
                        animation: 'spin 1s linear infinite'
                    }} />
                    <p style={{
                        fontSize: '1.08rem',
                        color: '#013e7d',
                        margin: 0,
                        fontWeight: 600
                    }}>Loading M-SPHERE...</p>
                </div>
                <style>
                    {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
                </style>
            </div>
        );
    }



    return (
        <div style={{ display: 'flex', height: '100vh', fontFamily: designTokens.typography.fontFamily }}>
            <StyledSidebar
                ref={sidebarRef}
                className={`app ${collapsed ? 'collapsed' : ''} ${isHoverExpanded ? 'hover-expanded' : ''}`}
                width="280px"
                collapsedWidth="80px"
                transitionDuration={300}
                collapsed={collapsed}
            >
                <Menu>
                    <BackButton to="/dashboard">
                        <FaArrowLeft />
                        Back to Home
                    </BackButton>

                    {currentMenu && groupedNavigation[currentMenu] &&
                        Object.entries(groupedNavigation[currentMenu]).map(([submenuName, modules]) => (
                            <SubMenu
                                key={submenuName}
                                label={submenuName}
                                icon={getSubMenuIcon(modules[0].module_route)}
                                defaultOpen={openSubmenus.includes(submenuName)}
                                onOpenChange={(open) => handleSubmenuToggle(submenuName, open)}
                            >
                                {Array.from(new Set(modules.map(JSON.stringify)), JSON.parse)
                                    .map((module) => (
                                        <MenuItem
                                            key={module.module_name}
                                            icon={getSubMenuIcon(module.module_route)}
                                            onClick={() => handleModuleSelect(
                                                submenuName,
                                                module.module_name,
                                                module.module_route
                                            )}
                                            className={selectedModule === module.module_name ? 'ps-active' : ''}
                                            active={selectedModule === module.module_name}
                                        >
                                            {module.module_name.trim()}
                                        </MenuItem>
                                    ))}
                            </SubMenu>
                        ))}
                    <SubMenu label="Menu" icon={<FaGlobe />}>
                        {uniqueMenus.map((menu) => (
                            <MenuItem
                                key={menu}
                                icon={getIcon(menu)}
                                onClick={() => handleMenuSelect(menu)}
                                active={currentMenu === menu}
                            >
                                {menu}
                            </MenuItem>
                        ))}
                    </SubMenu>
                </Menu>
            </StyledSidebar>

            <MainContent collapsed={collapsed}>
                <Breadcrumb className="mb-3">
                    <BreadcrumbItem>M-SPHERE</BreadcrumbItem>
                    <BreadcrumbItem>
                        {currentMenu || (menuName && menuName.replace(/-/g, ' '))}
                    </BreadcrumbItem>
                    {selectedSubmenu &&
                        <BreadcrumbItem>{selectedSubmenu}</BreadcrumbItem>
                    }
                    {selectedModule &&
                        <BreadcrumbItem active>{selectedModule}</BreadcrumbItem>
                    }
                </Breadcrumb>
                <div className="white-card">
                    <GlobalComponent
                        submenuRoute={selectedSubmenuRoute}
                        navigation={navigation}
                    />
                </div>
            </MainContent>
        </div>
    );
};

export default DynamicPageLayout;